<template>
  <div
    :id="id !== undefined ? id : 'sb-combobox-wrapper-' + uid"
    v-click-outside="hideList"
    class="sb-select"
    :class="{
      'sb-select--loading': isLoading,
      'sb-select--open': isOpen,
      'sb-select--inline': inline,
    }"
    @blur="$emit('search', undefined)"
  >
    <SbSelectInner
      ref="inner"
      :style="`anchor-name: --dialog-${uid};`"
      :data-testid="`${dataTestid}-inner`"
      :is-open="isOpen"
      :warning="warning"
      :error="error"
      :uid="uid"
      :is-disabled="isDisabled"
      :is-loading="isLoading"
      :item-label="itemLabel"
      :item-value="itemValue"
      :placeholder="placeholder.length ? placeholder : label"
      :left-icon="leftIcon"
      :left-icon-color="leftIconColor"
      :option-icon="selectedItem?.icon?.toString()"
      :loading-label="loadingLabel"
      :model-value="modelValue"
      :options="transformedOptions"
      :search-input="searchInput"
      :show-caption="showCaption"
      :item-caption="itemCaption"
      :use-avatars="useAvatars"
      :disable-filter="disableFilter || useMinibrowser"
      :clearable="clearable"
      :inline="inline"
      :is-user-select="isUserSelect"
      :labeled-by="labeledBy"
      :hidden-label="hiddenLabel"
      :input-id="inputId"
      :is-user-typing="isUserTyping"
      :helper-text="helperText"
      @click="handleDropdownDisplay"
      @search="handleSearchInput"
      @show-list="showList"
      @close-list="hideList"
      @keydown-enter="handleKeyDownEnter"
      @show-all-options="handleShowAllFilterOptions"
      @clear-selection="handleClearSelection"
      @focus-on-input="handleFocusOnInput"
      @blur-on-input="handleBlurOnInput"
    >
      <template #left-icon>
        <!-- @slot overwrite the left icon in Select Inner component -->
        <slot name="left-icon" />
      </template>

      <template #selection>
        <!-- @slot overwrite the selection slot in Select Inner component -->
        <slot name="selection" />
      </template>

      <template #rightIcon>
        <!-- @slot overwrite the right icon in Select Inner component -->
        <slot name="rightIcon" />
      </template>

      <!-- @slot overwrite the innerSelect slot in Select Inner component -->
      <slot name="innerSelect" />
    </SbSelectInner>

    <span
      v-if="helperText"
      :id="`sb-combobox-${uid}-helper-text`"
      class="sb-select__message sb-select__message--helper"
    >
      {{ helperText }}
    </span>

    <span v-if="showError" class="sb-select__message sb-select__message--error">
      {{ errorMessage }}
    </span>

    <SbSelectList
      v-if="isListRendered"
      ref="list"
      v-infinite-scroll="handleInfiniteScroll"
      :model-value="modelValue"
      :is-loading="isLoading"
      :search-input="searchInput"
      :item-label="itemLabel"
      :item-value="itemValue"
      :input-id="uid"
      :options="filteredOptions"
      :use-avatars="useAvatars"
      :no-data-text="noDataText"
      :emit-option="emitOption"
      :show-caption="showCaption"
      :item-caption="itemCaption"
      :is-loading-more="isLoadingMore"
      :loading-more-text="loadingMoreText"
      :is-option-disabled="isOptionDisabled"
      :data-testid="`${dataTestid}-list`"
      :navigational-select="navigationalSelect"
      @emit-value="handleEmitValue"
    >
      <template #list-item="scope">
        <!-- @slot controls the list item visibility -->
        <slot name="list-item" v-bind="scope" />
      </template>
    </SbSelectList>

    <!-- @slot define the location of the Minibrowser component -->
    <slot name="minibrowser" />
  </div>
</template>

<script lang="ts">
import { debounce } from 'throttle-debounce'
import { ref, watch, type PropType } from 'vue'
import { ClickOutside, InfiniteScroll } from '../../directives'

import {
  canUseDOM,
  includes,
  isString,
  isValidNumber,
  toLowerCase,
} from '../../utils'
import type { IconColor } from '../Icon/types'
import SbSelectInner from './components/SelectInner.vue'
import SbSelectList from './components/SelectList.vue'
import { getSearchTextFromSource } from './helpers'
import type { Option, SelectValue } from './types'
import type { IconName } from '../Icon'

export default {
  name: 'SbSelect',

  directives: {
    ClickOutside,
    InfiniteScroll,
  },

  components: {
    SbSelectInner,
    SbSelectList,
  },

  provide() {
    return {
      selectContext: () => this.selectContext,
    }
  },

  props: {
    /**
     * Defines which item is selected
     * @type {string|number}
     */
    modelValue: {
      type: [String, Number] as PropType<SelectValue>,
      default: null,
    },

    /**
     * Defines the debounce time to filter the options
     */
    filterDebounce: {
      type: Number,
      default: 800,
    },

    /**
     * Defines if the select is disabled
     */
    isDisabled: Boolean,

    /**
     * Defines if the select should emit the option object. See the "Emit Option" section for more details
     */
    emitOption: Boolean,

    /**
     * Defines if the select is loading
     */
    isLoading: Boolean,

    /**
     * Defines the loading label text
     */
    loadingLabel: {
      type: String,
      default: 'Loading...',
    },

    /**
     * Defines the placeholder text
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * @ignore
     * @deprecated Defines the label of the select
     */
    label: {
      // being deprecated, only here for backward compatibility
      type: String,
      default: '',
    },

    /**
     * Defines the left icon of the select
     */
    leftIcon: {
      type: String as PropType<IconName>,
      default: null,
    },

    /**
     * Defines the color of the left icon
     */
    leftIconColor: {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      type: String as PropType<IconColor>,
      default: null,
    },

    /**
     * Defines the options of the select. See the "Options" section for more details
     */
    options: {
      type: Array as PropType<Option[] | string[]>,
      default: () => [],
    },

    /**
     * Defines if the select will use avatars
     */
    useAvatars: Boolean,

    /**
     * Defines the label key of the options. See "Configuring Options" section for more details
     */
    itemLabel: {
      type: String,
      default: 'label',
    },

    /**
     * Defines the value key of the options. See "Configuring Options" section for more details
     */
    itemValue: {
      type: String,
      default: 'value',
    },

    /**
     * Defines the text when no data is found
     */
    noDataText: {
      type: String,
      default: 'Sorry, no result found.',
    },

    /**
     * Defines the error message
     */
    errorMessage: {
      type: String,
      default: '',
    },

    /**
     * Defines if the select has an error
     */
    error: Boolean,

    /**
     * Defines if the select has a warning state
     */
    warning: Boolean,

    /**
     * Defines if the select should show the caption
     */
    showCaption: Boolean,

    /**
     * Defines the caption key of the options. See "Configuring Options" section for more details
     */
    itemCaption: {
      type: String,
      default: 'caption',
    },

    /**
     * Defines if the select should use the infinite scroll. When this is enabled, the load-more event will be emitted when the user scrolls to the end of the list. See the "Infinite Scroll" section for more details
     */
    infiniteScroll: Boolean,

    /**
     * Defines if the select is loading more options. See the "Infinite Scroll" section for more details
     */
    isLoadingMore: Boolean,

    /**
     * Defines the text when the select is loading more options
     */
    loadingMoreText: {
      type: String,
      default: 'Loading more...',
    },

    renderOnOpen: Boolean,

    /**
     * Defines if a select item in the list should be disabled. See the "Configuring Options" section for more details
     */
    isOptionDisabled: {
      type: Function,
      default: () => false,
    },

    /**
     * Make the input readonly disabling the filter
     */
    disableFilter: Boolean,

    /**
     * Disables the internal filter functionality of the select
     */
    disableInternalFilter: Boolean,

    /**
     * Defines if the select could be clearable. When it is true, a clear icon button will be shown in the select
     */
    clearable: Boolean,

    /**
     * Defines if the select should be inline
     */
    inline: Boolean,

    /**
     * Defines if the select should be user-selectable
     */
    isUserSelect: Boolean,

    /**
     * Defines the accessible element that labels the select
     */
    labeledBy: {
      type: String,
      default: '',
    },

    /**
     * Defines the ID for the input in SelectInner component
     */
    inputId: {
      type: String,
      default: undefined,
    },

    /**
     * Defines a helper text message. This will be shown below the select component
     */
    helperText: {
      type: String,
      default: undefined,
    },

    /**
     * Defines wheather to wrap the select list in a nav or not.
     */
    navigationalSelect: Boolean,

    /**
     * Defines a hidden label/aria-label for the select component. Generally we should always have a visible label. This should only be used in rare cases.
     */
    hiddenLabel: {
      type: String,
      default: undefined,
    },

    // TODO convert to composition API, remove this prop, and replace with `& /* @vue-ignore */ DivHTMLAttributes`
    id: {
      type: String,
      default: undefined,
    },
  },

  emits: [
    'filter',
    'hide',
    'update:modelValue',
    'load-more',
    'search-input',
    'show',
    'blur',
    'search',
    'clear',
  ],

  setup(props, { emit }) {
    const isUserTyping = ref(false)
    const showAllFilterOptions = ref(true)
    const searchInput = ref<string | undefined>(undefined)

    const handleSearchInput = (value: string | undefined) => {
      searchInput.value = value
    }

    watch(
      searchInput,
      debounce(props.filterDebounce, () => {
        if (!isUserTyping.value) {
          return
        }

        const value = !showAllFilterOptions.value ? searchInput.value : ''
        /**
         * Emit when the user types in the search input
         *
         * @event search-input
         */
        emit('search-input', value)

        /**
         * Emit when the user types in the search input, and the filter is triggered
         *
         * @event filter
         */
        emit('filter', value)
      }),
    )

    return {
      isUserTyping,
      showAllFilterOptions,
      searchInput,
      handleSearchInput,
    }
  },

  data: () => ({
    isOpen: false,
    activeIndex: -1,
    listItems: [] as unknown as NodeListOf<HTMLLIElement>,
    innerElement: null as HTMLElement | null,
    vcoConfig: {
      detectIFrame: false,
    },
  }),

  computed: {
    dataTestid() {
      return this.$attrs['data-testid'] || 'sb-select'
    },

    selectContext() {
      return {
        // controls elements
        selectedItemIndex: this.selectedItemIndex,
        selectedItem: this.selectedItem,
        activeIndex: this.activeIndex,
        listItems: this.listItems,

        // methods to control the menu state
        hideList: this.hideList,
        showList: this.showList,
        focusAtIndex: this.focusAtIndex,
        focusOnSelectedItem: this.focusOnSelectedItem,
        focusOnFirstItem: this.focusOnFirstItem,
        focusOnLastItem: this.focusOnLastItem,
      }
    },

    selectedItem() {
      return this.transformedOptions.find((option) => {
        return option[this.itemValue] === this.modelValue
      })
    },

    selectedItemIndex() {
      return this.filteredOptions.findIndex((option) => {
        return option[this.itemValue] === this.modelValue
      })
    },

    useMinibrowser() {
      return !!this.$slots.minibrowser && !!this.innerElement
    },

    isListRendered() {
      const shouldRender = this.renderOnOpen ? this.isOpen : true
      return !this.useMinibrowser && shouldRender
    },

    filteredOptions() {
      if (!this.disableFilter && this.showAllFilterOptions) {
        return this.transformedOptions
      }

      if (
        this.hasValueToSearch &&
        !this.disableFilter &&
        !this.disableInternalFilter
      ) {
        return this.transformedOptions.filter((opt) => {
          return includes(
            toLowerCase(opt[this.itemLabel] as string),
            toLowerCase(this.searchInput as string),
          )
        })
      }

      return this.transformedOptions
    },

    hasValueToSearch() {
      return this.searchInput && this.searchInput.length > 0
    },

    transformedOptions() {
      return this.options.map((opt) => {
        if (typeof opt === 'object') {
          return opt
        }
        return { [this.itemLabel]: opt, [this.itemValue]: opt }
      })
    },

    showError() {
      return this.error && this.errorMessage
    },

    uid() {
      return this.$.uid.toString()
    },
  },

  watch: {
    activeIndex() {
      this.$_updateNavigation()
    },

    filteredOptions() {
      this.$nextTick(() => {
        this.$_loadListItems()
      })
    },

    modelValue(newValue: SelectValue) {
      if (newValue === null || newValue === undefined) {
        this.searchInput = ''
      }
    },
  },

  mounted() {
    this.$_loadListItems()

    this.$nextTick(() => {
      if (this.$refs.inner) {
        this.innerElement = (this.$refs.inner as typeof SbSelectInner).$el
      }
    })
  },

  methods: {
    handleShowAllFilterOptions(value: boolean) {
      this.showAllFilterOptions = value
    },
    /**
     * shows the items list
     */
    showList(shouldEmit = true) {
      if (this.isOpen) {
        return
      }

      this.isOpen = true
      if (this.selectedItemIndex) {
        this.activeIndex = this.selectedItemIndex
      }

      if (!this.searchInput?.length) {
        this.handleShowAllFilterOptions(true)
      }

      if (shouldEmit) {
        /**
         * Emit when the list is visible
         *
         * @event show
         */
        this.$emit('show')
      }
    },

    /**
     * hides the items list
     */
    hideList() {
      if (!this.isOpen) {
        return
      }

      this.isOpen = false
      /**
       * Emit when the list is hidden
       *
       * @event hide
       */
      this.$emit('hide', this.searchInput)

      this.resetSearchOnHide()
      this.handleShowAllFilterOptions(true)
    },

    /**
     * reset the search input value on select list hide
     */
    resetSearchOnHide() {
      if (
        !this.disableFilter &&
        (isString(this.modelValue) || isValidNumber(this.modelValue))
      ) {
        this.$nextTick(() => {
          this.searchInput =
            getSearchTextFromSource(this.modelValue, {
              label: this.itemLabel,
              value: this.itemValue,
              options: this.transformedOptions,
            }) ?? ''
        })
      }
    },

    /**
     * shows or hides the items list according to internal state
     */
    handleDropdownDisplay() {
      if (this.isDisabled || this.isLoading) {
        return
      }
      if (this.isOpen) {
        this.hideList()
      } else {
        this.showList()
      }
    },

    /**
     * emits the input event to make this component compatible with v-model directive
     * @param {Array<String>|String} value
     */
    handleEmitValue(value: string | string[]) {
      const emptyValue = ''
      const inputValue =
        getSearchTextFromSource(value, {
          label: this.itemLabel,
          value: this.itemValue,
          options: this.transformedOptions,
        }) ?? emptyValue
      this.searchInput =
        !this.disableFilter && !this.useAvatars ? inputValue : emptyValue
      this.handleShowAllFilterOptions(true)
      this.$nextTick(() => {
        this.$emit('update:modelValue', value)
      })
      this.$_focusInner()
      this.hideList()
    },

    /**
     * handle with keydown enter event from inner elemen
     * trying to select the first item in the array with single one item
     * or open the list if the list is not open
     */
    handleKeyDownEnter() {
      if (!this.isOpen) {
        this.focusOnSelectedItem()
        return
      }
      this.$_focusInner()
      this.hideList()
    },

    /**
     * set focus to first list item
     */
    focusOnFirstItem() {
      this.showList()

      this.activeIndex = 0

      this.$_updateNavigation()
    },

    /**
     * set focus to last list item
     */
    focusOnLastItem() {
      this.showList()

      this.activeIndex = this.listItems.length - 1
    },

    /**
     * set focus to the selected item, otherwise, to the first
     */
    focusOnSelectedItem() {
      this.showList()

      this.activeIndex =
        this.selectedItemIndex != -1 ? this.selectedItemIndex : 0

      this.$_updateNavigation()
    },

    /**
     * set focus a specific list item index
     * @param {Number} index Position index of menu list item
     */
    focusAtIndex(index: number) {
      this.$_updateTabIndex(index)

      this.activeIndex = index
    },

    /**
     * updates tab index for list items and
     * scroll to focused list item.
     * @param {Number} index Position index of menu list item
     */
    $_updateTabIndex(index: number) {
      if (this.listItems.length > 0) {
        const nodeAtIndex = this.listItems[index]

        this.listItems.forEach((node) => {
          if (node !== nodeAtIndex) {
            node.setAttribute('tabindex', '-1')
          }
        })
        if (!nodeAtIndex) {
          return
        }
        nodeAtIndex.setAttribute('tabindex', '0')
      }
    },

    /**
     * set all list items to tabindex -1
     */
    $_resetTabIndex() {
      if (this.listItems.length > 0) {
        this.listItems.forEach((node) => {
          node.setAttribute('tabindex', '-1')
        })
      }
    },

    /**
     * set focus to trigger button element
     */
    $_focusInner() {
      this.$nextTick(() => {
        const { $el, $refs } = this.$refs.inner as typeof SbSelectInner
        if (canUseDOM && $el instanceof HTMLElement) {
          $el.focus()
          if (!this.disableFilter) {
            $refs.search?.focus()
          }
        }
      })
    },

    /**
     * get all list item elements
     */
    $_loadListItems() {
      if (!this.useMinibrowser) {
        const menuNode =
          canUseDOM && (this.$refs?.list as typeof SbSelectList)?.$el

        if (menuNode) {
          this.listItems = menuNode.querySelectorAll('li')
        }
      }
    },

    /**
     * force update navigation index
     */
    $_updateNavigation() {
      if (this.activeIndex !== -1) {
        this.$nextTick(() => {
          this.listItems[this.activeIndex] &&
            this.listItems[this.activeIndex].focus()

          this.$_updateTabIndex(this.activeIndex)
        })

        return
      }

      this.$nextTick(() => {
        this.$_focusInner()

        this.$_resetTabIndex()
      })
    },

    /**
     * emit a load-more event when the user scroll till the end of the list of items
     */
    handleInfiniteScroll() {
      if (this.infiniteScroll) {
        /**
         * Emit when the user scrolls to the end of the list of items
         *
         * @event load-more
         */
        this.$emit('load-more')
      }
    },

    /**
     * clear the selected value by emitting two events: clear and update:modelValue
     */
    handleClearSelection() {
      this.searchInput = ''
      /**
       * Emit when the user clears the selection
       *
       * @event clear
       */
      this.$emit('clear')
      this.$emit('update:modelValue', null)
      this.hideList()
      this.$_focusInner()
    },

    handleFocusOnInput() {
      this.isUserTyping = true
    },

    handleBlurOnInput() {
      this.isUserTyping = false
    },
  },
}
</script>
